import axios from 'axios';
import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import {
  getMessaging,
  getToken,
  isSupported,
  onMessage,
  type MessagePayload,
} from '@firebase/messaging';
import toast from 'react-hot-toast';
import {
  getFirebaseWebConfig,
  getFirebaseWebVapidKey,
  isFirebaseWebPushConfigured,
} from '../config/firebaseClient';
import { REACT_APP_API_BASE_URL } from '../config/env';
import { registerAdminFcmToken as postFcmTokenToBackend } from '../services/MasterAdmin/masterAdminNotificationService';
import {
  ensureAdminNotificationAudioUnlock,
  playAdminNotificationTone,
  unlockAdminNotificationAudio,
} from './adminNotificationSound';

/** Stable per-browser id so logout can remove the correct server row. */
export const ADMIN_FCM_DEVICE_ID_KEY = 'vedika_admin_fcm_device_id';
export const ADMIN_FCM_LAST_STATUS_KEY = 'vedika_admin_fcm_status';

/** Must match the SW postMessage type in public/firebase-messaging-sw.js */
export const ADMIN_FCM_SW_TONE_MESSAGE = 'ADMIN_FCM_PLAY_TONE';

let messagingInstance: ReturnType<typeof getMessaging> | null = null;
let foregroundListenerAttached = false;

export function getOrCreateAdminDeviceId(): string {
  try {
    let id = localStorage.getItem(ADMIN_FCM_DEVICE_ID_KEY);
    if (!id) {
      id =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `web-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
      localStorage.setItem(ADMIN_FCM_DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    return `web-${Date.now()}`;
  }
}

function apiRoot(): string {
  return REACT_APP_API_BASE_URL || '/api';
}

/**
 * Call before clearing auth tokens (logout, 401). Uses raw axios so it does not depend on api interceptors.
 */
export async function unregisterAdminFcmBeforeClearAuth(): Promise<void> {
  const deviceId = localStorage.getItem(ADMIN_FCM_DEVICE_ID_KEY);
  const token = localStorage.getItem('token');
  if (!deviceId || !token) {
    console.log('[Admin FCM] Skip unregister: missing deviceId or token', {
      hasDeviceId: Boolean(deviceId),
      hasToken: Boolean(token),
    });
    return;
  }
  try {
    console.log('[Admin FCM] Unregistering token on logout', { deviceId });
    await axios.delete(
      `${apiRoot()}/master-admin/notifications/fcm-token/${encodeURIComponent(deviceId)}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    console.log('[Admin FCM] Unregister success', { deviceId });
  } catch {
    console.warn('[Admin FCM] Unregister failed');
    /* best-effort */
  }
}

function getFirebaseApp(): FirebaseApp {
  const config = getFirebaseWebConfig();
  if (getApps().length) return getApps()[0];
  return initializeApp(config);
}

/**
 * Request notification permission (if needed), obtain FCM token, POST to backend.
 * No-op if Firebase env is incomplete, messaging unsupported, or permission denied.
 */
export async function registerAdminFcmForLoggedInUser(): Promise<void> {
  console.log('[Admin FCM] Starting registration flow');
  if (!isFirebaseWebPushConfigured()) {
    const msg =
      '[Admin FCM] Firebase config missing. Set REACT_APP_FIREBASE_* and REACT_APP_FIREBASE_VAPID_KEY.';
    localStorage.setItem(ADMIN_FCM_LAST_STATUS_KEY, msg);
    throw new Error(msg);
  }
  if (typeof window === 'undefined') return;
  if (!(await isSupported())) {
    const msg = '[Admin FCM] Browser does not support Firebase Messaging.';
    localStorage.setItem(ADMIN_FCM_LAST_STATUS_KEY, msg);
    throw new Error(msg);
  }
  if (!('Notification' in window)) {
    const msg = '[Admin FCM] Notification API not available in this browser.';
    localStorage.setItem(ADMIN_FCM_LAST_STATUS_KEY, msg);
    throw new Error(msg);
  }

  let permission = Notification.permission;
  if (permission === 'denied') {
    const msg = '[Admin FCM] Notification permission is denied by browser.';
    localStorage.setItem(ADMIN_FCM_LAST_STATUS_KEY, msg);
    throw new Error(msg);
  }
  if (permission === 'default') {
    permission = await Notification.requestPermission();
  }
  if (permission !== 'granted') {
    const msg = '[Admin FCM] Notification permission not granted.';
    localStorage.setItem(ADMIN_FCM_LAST_STATUS_KEY, msg);
    throw new Error(msg);
  }

  const app = getFirebaseApp();
  console.log('[Admin FCM] Firebase app ready');
  const messaging = getMessaging(app);
  const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
  console.log('[Admin FCM] Service worker registered', {
    scope: registration.scope,
  });
  try {
    await registration.update();
  } catch {
    /* ignore */
  }

  const vapidKey = getFirebaseWebVapidKey();
  if (!vapidKey) {
    const msg = '[Admin FCM] REACT_APP_FIREBASE_VAPID_KEY is missing.';
    localStorage.setItem(ADMIN_FCM_LAST_STATUS_KEY, msg);
    throw new Error(msg);
  }

  const fcmToken = await getToken(messaging, {
    vapidKey,
    serviceWorkerRegistration: registration,
  });
  if (!fcmToken) {
    const msg = '[Admin FCM] Firebase returned empty token.';
    localStorage.setItem(ADMIN_FCM_LAST_STATUS_KEY, msg);
    throw new Error(msg);
  }

  console.log('[Admin FCM] Token generated', {
    tokenPreview: fcmToken.slice(0, 18),
    tokenLength: fcmToken.length,
  });
  await postFcmTokenToBackend({
    fcmToken,
    deviceId: getOrCreateAdminDeviceId(),
    platform: 'web',
  });
  localStorage.setItem(ADMIN_FCM_LAST_STATUS_KEY, 'FCM token registered');
}

const recentPushKeys = new Set<string>();

function handleIncomingAdminPush(payload: MessagePayload): void {
  const title =
    payload.notification?.title ||
    payload.data?.title ||
    'Vedika Admin';
  const body =
    payload.notification?.body ||
    payload.data?.body ||
    '';

  const messageId = payload.messageId || `${title}::${body}`;
  if (recentPushKeys.has(messageId)) {
    console.log('[Admin FCM] Suppressed duplicate push notification:', title);
    return;
  }

  recentPushKeys.add(messageId);
  setTimeout(() => {
    recentPushKeys.delete(messageId);
  }, 4000);

  unlockAdminNotificationAudio();
  playAdminNotificationTone();

  toast(`${title}${body ? `: ${body}` : ''}`, {
    duration: 8000,
    icon: '🔔',
  });

  if (Notification.permission === 'granted') {
    try {
      const notification = new Notification(title, {
        body,
        icon: '/favicon.ico',
        tag: payload.data?.notificationType || payload.data?.type || 'admin-notification',
        // Force a fresh OS alert when the same tag is reused.
        renotify: true,
        silent: false,
      } as NotificationOptions);
      notification.onclick = () => {
        window.focus();
        const link = payload.data?.adminPanelUrl || '/notifications';
        window.location.href = link;
        notification.close();
      };
    } catch {
      /* ignore duplicate notification errors */
    }
  }
}

/**
 * Show in-app toast + tone + native notification when a push arrives while the tab is open.
 * Also listens for SW postMessage so background pushes still play a tone when a tab is open.
 */
export function setupAdminFcmForegroundListener(): () => void {
  if (foregroundListenerAttached || typeof window === 'undefined') {
    return () => {};
  }
  if (!isFirebaseWebPushConfigured()) {
    return () => {};
  }

  const teardownAudioUnlock = ensureAdminNotificationAudioUnlock();

  const onSwMessage = (event: MessageEvent) => {
    if (event.data?.type === ADMIN_FCM_SW_TONE_MESSAGE) {
      unlockAdminNotificationAudio();
      playAdminNotificationTone();
    }
  };
  if (navigator.serviceWorker) {
    navigator.serviceWorker.addEventListener('message', onSwMessage);
  }

  void (async () => {
    try {
      if (!(await isSupported())) return;
      const app = getFirebaseApp();
      messagingInstance = getMessaging(app);
      onMessage(messagingInstance, (payload: MessagePayload) => {
        handleIncomingAdminPush(payload);
      });
      foregroundListenerAttached = true;
      console.log('[Admin FCM] Foreground listener attached');
    } catch (err) {
      console.warn('[Admin FCM] Foreground listener setup failed:', err);
    }
  })();

  return () => {
    foregroundListenerAttached = false;
    messagingInstance = null;
    teardownAudioUnlock();
    if (navigator.serviceWorker) {
      navigator.serviceWorker.removeEventListener('message', onSwMessage);
    }
  };
}

/** Re-register token when tab becomes visible or on a schedule (Firebase may rotate tokens). */
export function scheduleAdminFcmKeepAlive(): () => void {
  if (typeof window === 'undefined') return () => {};

  const refresh = () => {
    if (!localStorage.getItem('token')) return;
    registerAdminFcmForLoggedInUser().catch((err) => {
      console.warn('[Admin FCM] Keep-alive registration failed:', err);
    });
  };

  const onVisibility = () => {
    if (document.visibilityState === 'visible') {
      refresh();
    }
  };

  document.addEventListener('visibilitychange', onVisibility);
  const interval = window.setInterval(refresh, 6 * 60 * 60 * 1000);

  return () => {
    document.removeEventListener('visibilitychange', onVisibility);
    window.clearInterval(interval);
  };
}
